import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
  StyledEngineProvider,
  PaletteColor,
} from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import rtlPlugin from 'stylis-plugin-rtl'
import { prefixer } from 'stylis'
import {
  isDesktopSize,
  isPhoneOrTablet,
  SIMULATED_PHONE_WIDTH,
} from '$func/environmentInfo'
import { DirectionContext } from './root'
import { CUSTOM_SCROLL_CLASS } from '$setup/style/constants'

const {
  palette: { augmentColor },
} = createTheme()

const createColor = (mainColor: string) =>
  augmentColor({ color: { main: mainColor } })

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

interface TypographyCustomVariants {
  header: React.CSSProperties
  headerMedium: React.CSSProperties
  headerRegular: React.CSSProperties
  titleBold: React.CSSProperties
  titleMedium: React.CSSProperties
  titleRegular: React.CSSProperties
  subtitleBold: React.CSSProperties
  subtitleMedium: React.CSSProperties
  subtitleRegular: React.CSSProperties
  messageBold?: React.CSSProperties
  h1?: React.CSSProperties
  h2?: React.CSSProperties
  h3?: React.CSSProperties
  subtitle1?: React.CSSProperties
  subtitle2?: React.CSSProperties
  subtitle3?: React.CSSProperties
  subtitle4?: React.CSSProperties
  body1?: React.CSSProperties
  body2?: React.CSSProperties
  body3?: React.CSSProperties
  paragraph1?: React.CSSProperties
  paragraph2?: React.CSSProperties
  caption1?: React.CSSProperties
  caption2?: React.CSSProperties
}

export type TypographyCustomVariant = keyof TypographyCustomVariants

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false
    sm: false
    md: false
    lg: false
    xl: false
    mobile: true
    desktop: true
  }

  interface Palette {
    semiBlack: string
    gray: string
    lightGray: string
    veryVeryLightGray: string
    white: string
    primary700: string
    primary500: string
    primary300: string
    primary100: string
    primary50: string
    neutral: {
      main: string
      light: string
      dark: string
      contrastText: string
    }

    secondary700: string
    secondary500: string
    secondary300: string
    secondary100: string
    secondary50: string

    success700: string
    success500: string
    success300: string
    success100: string

    error700: string
    error500: string
    error300: string
    error100: string

    neutral1000: string
    neutral900: string
    neutral800: string
    neutral700: string
    neutral600: string
    neutral500: string
    neutral400: string
    neutral300: string
    neutral200: string
    neutral100: string
    neutral000: string
    yellow700: string
    yellow500: string
    yellow100: string

    live500: string
    liveGradientL: string
    liveGradientH: string

    tribune500: string
    tribuneGradientH: string
    tribuneGradientL: string

    meetingGradientL: string
    meetingGradientH: string
    meetingGradientSuccess: string
    meetingGradientGray: string
    storyGradientH: string

    backdrop: string
    buttonTransparent: string
    lightChatSenderGradient: string

    veryLightGray: PaletteColor
    semiLightGray: PaletteColor
    black: PaletteColor
  }

  interface PaletteOptions {
    semiBlack: string
    gray: string
    lightGray: string
    veryVeryLightGray: string
    white: string

    neutral: {
      main: string
      light: string
      dark: string
      contrastText: string
    }

    primary700: string
    primary500: string
    primary300: string
    primary100: string
    primary50: string

    secondary700: string
    secondary500: string
    secondary300: string
    secondary100: string
    secondary50: string

    success700: string
    success500: string
    success300: string
    success100: string

    error700: string
    error500: string
    error300: string
    error100: string

    neutral1000: string
    neutral900: string
    neutral800: string
    neutral700: string
    neutral600: string
    neutral500: string
    neutral400: string
    neutral300: string
    neutral200: string
    neutral100: string
    neutral000: string
    yellow700: string
    yellow500: string
    yellow100: string

    live500: string
    liveGradientL: string
    liveGradientH: string

    tribune500: string
    tribuneGradientH: string
    tribuneGradientL: string

    meetingGradientL: string
    meetingGradientH: string
    meetingGradientSuccess: string
    meetingGradientGray: string

    storyGradientH: string

    backdrop: string
    buttonTransparent: string
    lightChatSenderGradient: string
    veryLightGray: string
    semiLightGray: string
    black: PaletteColor
  }

  interface TypographyVariants extends TypographyCustomVariants {}

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions extends TypographyCustomVariants {}
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true
  }
}
declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    neutral000: true
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    header: true
    headerMedium: true
    headerRegular: true
    titleBold: true
    titleMedium: true
    titleRegular: true
    subtitleBold: true
    subtitleMedium: true
    subtitleRegular: true
    messageBold: true
    h1: true
    h2: true
    h3: true
    subtitle1: true
    subtitle2: true
    subtitle3: true
    subtitle4: true
    body1: true
    body2: true
    body3: true
    paragraph1: true
    paragraph2: true
    paragraph3: true
    caption1: true
    caption2: true
  }
}

declare module '@mui/material/CircularProgress' {
  interface CircularProgressPropsColorOverrides {
    white: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    gray: true
  }
}

declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    neutral: true
  }
}

export const Palette = {
  primary700: '#1B8CC0',
  primary500: '#009DE6',
  primary300: '#99DDFF',
  primary100: '#C8E8F8',
  primary50: '#E5F6FF',

  secondary700: '#412EB8',
  secondary500: '#6B54F7',
  secondary300: '#8C7CF3',
  secondary100: '#C1B9F9',
  secondary50: '#EBE8FD',

  success700: '#207C0E',
  success500: '#1DB200',
  success300: '#70D65C',
  success100: '#CEEFC8',

  error700: '#AE2C13',
  error500: '#E92500',
  error300: '#EE8A77',
  error100: '#F9D8D2',

  neutral1000: '#080D11',
  neutral900: '#15212B',
  neutral800: '#303336',
  neutral700: '#50585F',
  neutral600: '#61666B',
  neutral500: '#868C92',
  neutral400: '#B4B8BC',
  neutral300: '#D3D9DE',
  neutral200: '#E1E8EF',
  neutral100: '#E6F0F9',
  neutral000: '#FFFFFF',
  yellow700: '#DEBD50',
  yellow500: '#FFC400',
  yellow100: '#F3E7BF',

  live500: '#FF1C7A',
  liveGradientL: 'linear-gradient(270deg, #FFE0ED 0%, #FFA3C9 101.53%)',
  liveGradientH: 'linear-gradient(180deg, #FF61A3 0%, #FF1A79 100%)',

  tribune500: '#6054F7',
  tribuneGradientH: 'linear-gradient(180deg, #7C63F8 0%, #5454F7 100%)',
  tribuneGradientL: 'linear-gradient(270deg, #E5E0FF 0%, #A3A3FF 101.53%)',

  meetingGradientL: 'linear-gradient(270deg, #CCF0FF 0%, #A3E3FF 101.53%)',
  meetingGradientH: 'linear-gradient(180deg, #1BBBFE 0%, #01A0E2 100%)',
  meetingGradientSuccess: 'linear-gradient(90deg, #15C15A 0%, #12A14B 100%)',
  meetingGradientGray: 'linear-gradient(90deg, #798386 0%, #61686B 100%)',
  storyGradientH: 'linear-gradient(360deg, #5458F7 0%,  #1ABAFD 100%)',

  backdrop:
    'linear-gradient(0deg, rgba(255, 214, 0, 0.10) 0%, rgba(255, 214, 0, 0.10) 100%), rgba(0, 0, 0, 0.50)',
  buttonTransparent: 'rgba(80, 91, 95, 0.50)',
  lightChatSenderGradient: 'linear-gradient(180deg, #654FF7 0%, #6945F7 100%)',
}

enum FONT_WEIGHT {
  BOLD = 700,
  MEDIUM = 500,
  REGULAR = 400,
  SEMI_BOLD = 450,
}
const defaultTheme = createTheme()

export const theme = createTheme({
  direction: 'rtl',
  breakpoints: {
    values: {
      mobile: 0,
      desktop: 1080,
    },
  },
  shape: {
    borderRadius: 4,
  },
  typography: {
    header: {
      fontSize: 16,
      fontWeight: FONT_WEIGHT.BOLD,
      lineHeight: 1.75,
    },
    headerMedium: {
      fontSize: 16,
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: 1.75,
    },
    headerRegular: {
      fontSize: 16,
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: 1.75,
    },
    titleBold: {
      fontSize: 14,
      fontWeight: FONT_WEIGHT.BOLD,
      lineHeight: 1.75,
    },
    titleMedium: {
      fontSize: 14,
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: 1.75,
    },
    titleRegular: {
      fontSize: 14,
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: 1.75,
    },
    subtitleBold: {
      fontSize: 12,
      fontWeight: FONT_WEIGHT.BOLD,
      lineHeight: 1.5,
    },
    subtitleMedium: {
      fontSize: 12,
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: 1.5,
    },
    subtitleRegular: {
      fontSize: 12,
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: 1.5,
    },
    messageBold: {
      fontSize: 13,
      fontWeight: FONT_WEIGHT.BOLD,
      lineHeight: '20px',
    },
    h1: {
      fontSize: '24px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '32px',
    },
    h2: {
      fontSize: '20px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '28px',
    },
    h3: {
      fontSize: '18px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '28px',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '24px',
    },
    subtitle2: {
      fontSize: '15px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '20px',
    },
    subtitle3: {
      fontSize: '14px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '20px',
    },
    subtitle4: {
      fontSize: '13px',
      fontWeight: FONT_WEIGHT.MEDIUM,
      lineHeight: '18px',
    },
    body1: {
      fontSize: '16px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '24px',
    },
    body2: {
      fontSize: '14px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '20px',
    },
    body3: {
      fontSize: '13px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '16px',
    },
    paragraph1: {
      fontSize: '16px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '28px',
      letterSpacing: '0.1px',
    },
    paragraph2: {
      fontSize: '14px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '24px',
      letterSpacing: '0.1px',
    },
    paragraph3: {
      fontSize: '13px',
      fontWeight: FONT_WEIGHT.REGULAR,
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
    caption1: {
      fontSize: '12px',
      fontWeight: FONT_WEIGHT.SEMI_BOLD,
      lineHeight: '16px',
    },
    caption2: {
      fontSize: '11px',
      fontWeight: FONT_WEIGHT.SEMI_BOLD,
      lineHeight: '16px',
    },
  },
  palette: {
    primary: {
      main: Palette.primary700,
      light: Palette.primary300,
      contrastText: Palette.neutral000,
    },
    secondary: {
      main: Palette.primary500,
      light: Palette.primary100,
      contrastText: Palette.neutral900,
    },
    error: {
      main: Palette.error500,
      light: Palette.error100,
      contrastText: Palette.neutral000,
    },
    neutral: {
      main: Palette.neutral500,
      light: Palette.neutral200,
      dark: Palette.neutral600,
      contrastText: Palette.neutral000,
    },
    success: {
      main: Palette.success500,
      light: Palette.success100,
    },
    text: {
      primary: Palette.neutral800,
      secondary: Palette.neutral700,
      disabled: Palette.neutral500,
    },
    semiBlack: '#353535',
    gray: '#818181',
    lightGray: '#CCCCCC',
    veryVeryLightGray: '#F6F6F6',
    white: '#fff',
    primary700: Palette.primary700,
    primary500: Palette.primary500,
    primary300: Palette.primary300,
    primary100: Palette.primary100,
    primary50: Palette.primary50,

    secondary700: Palette.secondary700,
    secondary500: Palette.secondary500,
    secondary300: Palette.secondary300,
    secondary100: Palette.secondary100,
    secondary50: Palette.secondary50,

    success700: Palette.success700,
    success500: Palette.success500,
    success300: Palette.success300,
    success100: Palette.success100,

    error700: Palette.error700,
    error500: Palette.error500,
    error300: Palette.error300,
    error100: Palette.error100,

    neutral1000: Palette.neutral1000,
    neutral900: Palette.neutral900,
    neutral800: Palette.neutral800,
    neutral700: Palette.neutral700,
    neutral600: Palette.neutral600,
    neutral500: Palette.neutral500,
    neutral400: Palette.neutral400,
    neutral300: Palette.neutral300,
    neutral200: Palette.neutral200,
    neutral100: Palette.neutral100,
    neutral000: Palette.neutral000,
    yellow700: Palette.yellow700,
    yellow500: Palette.yellow500,
    yellow100: Palette.yellow100,

    live500: Palette.live500,
    liveGradientL: Palette.liveGradientL,
    liveGradientH: Palette.liveGradientH,

    tribune500: Palette.tribune500,
    tribuneGradientL: Palette.tribuneGradientL,
    tribuneGradientH: Palette.tribuneGradientH,

    meetingGradientGray: Palette.meetingGradientGray,
    meetingGradientH: Palette.meetingGradientH,
    meetingGradientL: Palette.meetingGradientL,
    meetingGradientSuccess: Palette.meetingGradientSuccess,
    storyGradientH: Palette.storyGradientH,

    backdrop: Palette.backdrop,
    buttonTransparent: Palette.buttonTransparent,
    lightChatSenderGradient: Palette.lightChatSenderGradient,
    veryLightGray: '#f3f3f3',
    semiLightGray: '#B2B2B2',
    black: createColor('#000000'),
  },
  shadows: [
    'none',
    '0px 2px 3px 0px rgba(0, 0, 0, 0.05)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 0px 4px 0px rgba(0, 0, 0, 0.10)',
    '0px 0px 8px 0px rgba(0, 0, 0, 0.20)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
    '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
  ],
  components: {
    MuiTypography: {
      defaultProps: {
        color: 'neutral800',
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          subtitle1: 'h4',
          subtitle2: 'h5',
          subtitle3: 'h6',
          body1: 'p',
          body2: 'p',
          body3: 'p',
          paragraph1: 'p',
          paragraph2: 'p',
          caption1: 'span',
          caption2: 'span',
        },
      },
      styleOverrides: {
        root: {
          letterSpacing: 0,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          textAlign: 'inherit',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          textAlign: 'inherit',
          // ['@media (hover: none)']: {
          //   '&:hover': {
          //     backgroundColor: 'transparent', // This can be used for non-touchable devices to avoid hover effect
          //   },
          // },
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          padding: 2,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: Palette.backdrop,
        },
      },
    },
    // TODO: Keep till implementing desktop layout
    ...(!isPhoneOrTablet &&
      !isDesktopSize() && {
        MuiPaper: {
          styleOverrides: {
            root: {
              maxWidth: SIMULATED_PHONE_WIDTH,
              margin: 'auto',
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            container: {
              maxWidth: SIMULATED_PHONE_WIDTH,
              margin: 'auto',
            },
          },
        },
      }),
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: Palette.neutral100,
          background: 'none',
          '&.MuiDivider-inset': {
            margin: '0px 66px 0px 0px',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        position: 'static',
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 50,
          padding: '0 7px',

          [defaultTheme.breakpoints.up('sm')]: {
            minHeight: 50,
            padding: '0 7px',
          },
          [defaultTheme.breakpoints.up('md')]: {
            minHeight: 50,
            padding: '0 7px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          margin: '0 -4px 0 4px',
        },
        root: ({ ownerState, theme }) => ({
          borderRadius: theme.shape.borderRadius * 3,
          boxShadow: 'none',
          '&:hover, &:active': {
            boxShadow: 'none',
          },
          ...(ownerState.size === 'medium' && {
            height: '48px',
            ...theme.typography.subtitle1,
          }),
          ...(ownerState.size === 'small' && {
            // height: '40px',
            ...theme.typography.subtitle3,
          }),
          ...(ownerState.variant === 'contained' && {
            ...(ownerState.disabled && {
              backgroundColor: theme.palette.neutral400,
              color: theme.palette.neutral000,
            }),

            ...(ownerState.color === 'secondary' && {
              backgroundColor: Palette.secondary500,
              color: theme.palette.secondary.contrastText,
              '&:hover': {
                backgroundColor: Palette.secondary700,
              },
            }),
          }),
          ...(ownerState.variant === 'outlined' && {
            ...(ownerState.disabled && {
              borderColor: theme.palette.neutral400,
              color: theme.palette.neutral400,
            }),
          }),
          ...(ownerState.variant === 'text' && {
            ...(ownerState.disabled && {
              color: theme.palette.neutral400,
            }),
          }),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          color: 'inherit',
          borderRadius: theme.shape.borderRadius * 8,
          '& .MuiTouchRipple-root': {
            '& span': {
              borderRadius: '100%',
              transform: 'scale(1.3)',
            },
          },
          ...(ownerState.size === 'medium' && {
            width: '40px',
            height: '40px',
          }),
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          color: 'neutral300',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 'unset',
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        scrollButtons: false,
        textColor: 'secondary',
        indicatorColor: 'secondary',
      },
      styleOverrides: {
        root: {
          minHeight: '48px',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          width: '56px',
          height: '56px',
          ...(ownerState.variant === 'circular' &&
            ownerState.color === 'secondary' && {
              backgroundColor: Palette.neutral200,
              color: Palette.neutral800,
              '&:hover': {
                backgroundColor: Palette.neutral300,
              },
            }),
          ...(ownerState.variant === 'circular' &&
            ownerState.color === 'neutral' && {
              backgroundColor: Palette.neutral700,
              color: Palette.neutral000,
              '&:hover': {
                backgroundColor: Palette.neutral900,
              },
            }),
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          padding: '8px 16px',
          color: 'gray',

          '&.Mui-selected': {
            color: 'neutral700',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
          direction: 'rtl',
        },
        body: {
          margin: 0,
          overflow: 'hidden',
          // TODO: Remove
          backgroundColor: Palette.neutral300,
          height: '100%',
          userSelect: 'none',

          [`.${CUSTOM_SCROLL_CLASS}`]: {
            scrollbarWidth: 'auto',
            scrollbarColor: `${Palette.neutral400} transparent`,
          },

          '@supports not (scrollbar-width: auto)': {
            [`.${CUSTOM_SCROLL_CLASS}`]: {
              '-webkit-overflow-scrolling': 'touch',
            },

            // Chrome & Safari
            [`.${CUSTOM_SCROLL_CLASS}::-webkit-scrollbar`]: {
              width: '4px',
              // For horizontal scrollbar
              height: '4px',
            },

            [`.${CUSTOM_SCROLL_CLASS}::-webkit-scrollbar-thumb`]: {
              background: Palette.neutral300,
              // `box-shadow` prevents repaint on macOS when hovering out of scrollable container
              boxShadow: '0 0 1px rgba(255, 255, 255, 0.01)',
            },
            [`.${CUSTOM_SCROLL_CLASS}::-webkit-scrollbar-track`]: {
              backgroundColor: 'transparent',
            },
            [`.${CUSTOM_SCROLL_CLASS}::-webkit-scrollbar-button`]: {
              display: 'none',
            },
          },
        },
        code: {
          fontFamily: 'monospace !important',
        },
        '*': {
          fontFamily: 'iranyekanx !important',
        },
        '#root': {
          height: '100%',
          backgroundColor: Palette.primary500,
        },
        '.MuiDrawer-root': {
          transform: 'translate(0)',
          maxWidth: '1680px',
          margin: 'auto',
        },
      },
    },
  },
})

interface Props {
  children: JSX.Element
}

const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer, rtlPlugin],
})

/* <CssBaseline /> */
export function ThemeProvider({ children }: Props) {
  const { i18n } = useTranslation()
  const dir = i18n.language === 'en' ? 'ltr' : 'rtl'

  const providedChildren = (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  )
  return (
    <DirectionContext.Provider value={dir}>
      <StyledEngineProvider injectFirst>
        {dir === 'ltr' ? (
          <CacheProvider value={cacheLtr}>{providedChildren}</CacheProvider>
        ) : (
          providedChildren
        )}
      </StyledEngineProvider>
    </DirectionContext.Provider>
  )
}
